import { dfdClass } from './utils/dfdRules';
import i18n from '../../i18n';
/**
 * 삽입 메뉴·툴박스가 공유하는 노드 유형 카탈로그.
 * 엣지의 + 버튼으로 "두 노드 사이에 끼워 넣을 수 있는" 유형만 담는다 — 그룹 컨테이너(서브플로/반복그룹)는 제외.
 */
export interface NodeCatalogEntry {
  type: string;
  category: 'data' | 'process' | 'control' | 'exec';
}

/**
 * 노드 유형 → 화면에 보일 유형명 (workflow:nodeType.<유형>). 모르는 유형이면 타입 문자열 그대로.
 * 툴박스·삽입 메뉴·노드 안의 유형명·새 노드의 기본 이름이 모두 이것을 쓴다 — 한 곳에서만 정한다.
 * 새 노드의 기본 이름은 만드는 사람의 언어로 붙는다. 이미 저장된 노드 이름은 데이터라 바뀌지 않는다.
 */
export function typeLabelOf(type?: string): string {
  const ty = String(type || '');
  const key = `workflow:nodeType.${ty}`;
  return ty && i18n.exists(key) ? i18n.t(key) : ty;
}

/** 툴박스·삽입 메뉴의 부류 이름 */
export function categoryLabelOf(category: NodeCatalogEntry['category']): string {
  return i18n.t(`workflow:toolbox.${category}`);
}

/**
 * **사용 보류** 노드 유형 — 툴박스·삽입 메뉴에 「보류」 리본을 붙이고 흐리게 보인다(막지는 않는다).
 *
 * 지금은 비어 있다. 2026-09-23 에 데이터처리를 뺀 프로세스 노드(필터·조인·집계·정렬/중복제거·룩업·품질검사)를
 * 보류로 두었다가 같은 날 모두 해제했다(현장 요청). 다시 보류할 유형이 생기면 여기에 넣기만 하면 된다.
 */
export const ON_HOLD_NODE_TYPES = new Set<string>([]);
export const isOnHoldType = (type?: string): boolean => ON_HOLD_NODE_TYPES.has(String(type || ''));

export const INSERTABLE_NODE_TYPES: NodeCatalogEntry[] = [
  { type: 'csvData', category: 'data' },
  { type: 'rdbData', category: 'data' },
  { type: 'innerData', category: 'data' },
  { type: 'transformLoad', category: 'process' },
  { type: 'filter', category: 'process' },
  { type: 'join', category: 'process' },
  { type: 'aggregate', category: 'process' },
  { type: 'sort', category: 'process' },
  { type: 'lookup', category: 'process' },
  { type: 'compare', category: 'process' },
  { type: 'quality', category: 'process' },
  { type: 'branch', category: 'control' },
  { type: 'merge', category: 'control' },
  { type: 'userConfirm', category: 'control' },
  { type: 'runWorkflow', category: 'exec' },
  { type: 'shellCommand', category: 'exec' },
  { type: 'sqlScript', category: 'exec' },
];


/**
 * 노드 도형을 정하는 CSS 클래스 — **툴박스와 캔버스가 같은 함수를 쓴다.**
 *
 * 예전에는 두 곳이 따로 판단해서, 실행 노드가 툴박스에서는 사각형인데 캔버스에서는 처리 노드와 똑같은
 * 알약 모양으로 그려졌다(현장 2026-09-22). 부류마다 도형이 하나씩 있어야 그림을 읽을 수 있다.
 *
 *   데이터  — 둥근 사각형
 *   처리    — 알약
 *   실행    — 사각형 + 좌우 세로줄 (순서도의 '미리 정의된 처리': 다른 곳에 정의된 일을 불러 쓴다)
 *   제어    — 알약 (흐름을 가르거나 모으는 통과점이라 처리와 같은 결로 둔다)
 */
export function shapeClassOf(type?: string): string {
  const cat = INSERTABLE_NODE_TYPES.find((e) => e.type === String(type || ''))?.category;
  if (cat === 'exec') return 'rounded-md node-shape-exec';
  if (cat === 'process' || cat === 'control') return 'rounded-full';
  return 'rounded-lg';
}

/**
 * DFD 규칙으로 삽입 가능 여부 — 이웃(엣지 삽입이면 양쪽 노드, 노드 옆 추가면 그 노드)과 같은 부류(데이터↔데이터, 처리↔처리)는 붙일 수 없다.
 * 제어/실행 노드는 이웃을 제약하지 않는다. 반환: 불가 사유(문자열) 또는 null(가능)
 */
export function insertBlockedReason(type: string, neighborTypes: string[]): string | null {
  const cls = dfdClass(type);
  if (cls !== 'data' && cls !== 'process') return null;
  for (const nt of neighborTypes) {
    if (dfdClass(nt) === cls) return cls === 'data' ? i18n.t('workflow:insert.dataNextToData') : i18n.t('workflow:insert.processNextToProcess');
  }
  return null;
}
